import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Alert, Button, Input, notification } from "antd";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";
import { validatePassword } from "../../utils/validatePassword";

import "./PasswordResetSetupPage.css";

export const PasswordResetSetupPage = () => {
  const { t } = useTranslation();
  usePageTitle(t("passwordRecovery.setup.title"));
  const navigate = useNavigate();
  const { token: tokenParam } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = tokenParam ?? searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { pending: loading, run: runChange } = usePendingAction();
  const [tokenError, setTokenError] = useState(false);

  const passwordInvalid = submitted && password && !validatePassword(password);
  const passwordMismatch =
    submitted && password && confirmPassword && password !== confirmPassword;

  async function handleChange() {
    setSubmitted(true);
    if (
      !password ||
      !validatePassword(password) ||
      !confirmPassword ||
      password !== confirmPassword
    )
      return;
    await runChange(async () => {
      try {
        const result = await api.auth.setupPassword(token, password);
        if (result.ok) {
          navigate("/password/reset/success");
        } else if (
          result.code === "wrong_token" ||
          result.code === "token_expired"
        ) {
          setTokenError(true);
        } else {
          notification.error({ message: t("errors.shared.common_error") });
        }
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      }
    });
  }

  return (
    <AuthCard>
      <h1>{t("passwordRecovery.setup.title")}</h1>
      <p>{t("passwordRecovery.setup.description")}</p>

      {tokenError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("passwordRecovery.setup.tokenErrorTitle")}
          description={
            <Trans
              i18nKey="passwordRecovery.setup.tokenErrorBody"
              components={{ lnk: <Link to="/password/reset" /> }}
            />
          }
        />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleChange();
        }}
      >
        <Input.Password
          placeholder={t("passwordRecovery.setup.newPassword")}
          size="large"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          status={
            submitted && (!password || passwordInvalid) ? "error" : undefined
          }
        />
        {submitted && !password && (
          <div className="password-reset-setup__field-error">
            {t("signup.details.required")}
          </div>
        )}
        {passwordInvalid && (
          <div className="password-reset-setup__field-error">
            {t("signup.details.passwordRequirements")}
          </div>
        )}

        <Input.Password
          placeholder={t("passwordRecovery.setup.confirmPassword")}
          size="large"
          style={{ marginTop: 12 }}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          status={
            submitted && (!confirmPassword || passwordMismatch)
              ? "error"
              : undefined
          }
        />
        {submitted && !confirmPassword && (
          <div className="password-reset-setup__field-error">
            {t("signup.details.required")}
          </div>
        )}
        {passwordMismatch && (
          <div className="password-reset-setup__field-error">
            {t("signup.details.passwordMismatch")}
          </div>
        )}

        <Button
          type="primary"
          size="large"
          block
          loading={loading}
          style={{ marginTop: 24, width: "fit-content" }}
          onClick={handleChange}
        >
          {t("passwordRecovery.setup.changePassword")}
        </Button>
      </form>

      <div style={{ marginTop: 16, fontSize: 14 }}>
        <Trans
          i18nKey="passwordRecovery.setup.goBackTo"
          components={{ lnk: <Link to="/login" /> }}
        />
      </div>
    </AuthCard>
  );
};
